// src/components/WatchPartyCard.js
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

function WatchPartyCard({
  partyId,
  animeTitle,
  hostName,
  avatarUrl,
  platform,
  slotsUsed,
  slotsTotal,
  isLive,
  isPublic = true,
}) {
  const navigate = useNavigate();

  const used = Number(slotsUsed || 0);
  const total = Number(slotsTotal || 0);
  const seatsLeft = total - used;
  const isFull = seatsLeft <= 0;
  const fill = useMemo(() => {
    if (!total) return 0;
    return Math.min(100, Math.max(0, (used / total) * 100));
  }, [used, total]);

  const handleClick = () => {
    navigate(`/watch-party/${partyId}`);
  };

  const platformMeta = useMemo(() => {
    const p = String(platform || "").toUpperCase();

    const cls =
      p === "YOUTUBE"
        ? "bg-red-600/90 border border-red-500/50 text-white"
        : p === "VIMEO"
        ? "bg-cyan-500/90 border border-cyan-400/50 text-white"
        : p === "DAILYMOTION"
        ? "bg-blue-500/90 border border-blue-400/50 text-white"
        : p === "CRUNCHYROLL"
        ? "bg-orange-500/90 border border-orange-400/50 text-white"
        : p === "NETFLIX"
        ? "bg-red-600/90 border border-red-500/50 text-white"
        : p === "HULU"
        ? "bg-green-500/90 border border-green-400/50 text-white"
        : p === "DISNEY_PLUS"
        ? "bg-blue-600/90 border border-blue-500/50 text-white"
        : p === "FUNIMATION"
        ? "bg-purple-600/90 border border-purple-500/50 text-white"
        : "bg-black/70 border border-white/20 text-slate-100";

    const label =
      p === "YOUTUBE"
        ? "YouTube"
        : p === "VIMEO"
        ? "Vimeo"
        : p === "DAILYMOTION"
        ? "Dailymotion"
        : p === "SELF_HOSTED"
        ? "Self-Hosted"
        : p === "CRUNCHYROLL"
        ? "Crunchyroll"
        : p === "NETFLIX"
        ? "Netflix"
        : p === "HULU"
        ? "Hulu"
        : p === "DISNEY_PLUS"
        ? "Disney+"
        : p === "FUNIMATION"
        ? "Funimation"
        : platform;

    return { cls, label };
  }, [platform]);

  return (
    <button
      onClick={handleClick}
      className="
        group relative overflow-hidden w-full
        rounded-xl sm:rounded-2xl
        bg-white/6 border border-white/16 backdrop-blur-xl
        px-3 py-2.5 sm:px-3 sm:py-2.5
        text-left text-[11px] text-slate-100
        transition duration-200
        hover:bg-white/10 hover:border-emerald-300/85 hover:-translate-y-0.5
        active:scale-[0.98]
      "
    >
      {/* subtle light streak on hover */}
      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="absolute -left-10 top-0 h-full w-16 bg-gradient-to-b from-emerald-400/0 via-emerald-400/25 to-emerald-400/0 blur-xl rotate-6" />
      </div>

      {/* =========================
          MOBILE LAYOUT (<sm)
          Fix: stack content so nothing fights for width
         ========================= */}
      <div className="sm:hidden">
        {/* Row 1: avatar + host + status */}
        <div className="flex items-center gap-2">
          <img
            src={avatarUrl}
            alt={hostName}
            className="h-7 w-7 rounded-full border border-white/40 object-cover flex-shrink-0"
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[8px] uppercase tracking-[0.16em] text-slate-400">
                Host
              </span>
              <span className="text-[10px] font-medium truncate">{hostName}</span>
            </div>
          </div>

          <span
            className={`
              inline-flex items-center gap-1 rounded-full px-2 py-0.5
              text-[8px] uppercase tracking-[0.18em] flex-shrink-0
              ${
                isLive
                  ? "bg-red-500 text-white"
                  : isFull
                  ? "bg-slate-600 text-slate-50"
                  : "bg-emerald-400 text-slate-950"
              }
            `}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white" />
            {isLive ? "Live" : isFull ? "Full" : "Idle"}
          </span>
        </div>

        {/* Row 2: anime title (full width) */}
        <p className="mt-2 text-[11px] font-semibold text-slate-50 truncate">
          {animeTitle}
        </p>

        {/* Row 3: chips (wrap + clamp widths so they never push out) */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[8px] uppercase tracking-[0.16em] ${
            isPublic
              ? "bg-slate-950/80 border border-emerald-400/40 text-emerald-200"
              : "bg-slate-950/80 border border-purple-400/40 text-purple-200"
          }`}>
            {isPublic ? "Public" : "Private"}
          </span>

          <span
            className={`rounded-full px-2 py-0.5 text-[8px] uppercase tracking-[0.16em] ${platformMeta.cls} max-w-[60%] truncate`}
            title={platformMeta.label}
          >
            {platformMeta.label}
          </span>

          <span className="text-[9px] text-slate-300 ml-auto">
            <span className="text-slate-400">{used}/{total}</span> joined
          </span>
        </div>

        {/* Row 4: seats + bar */}
        <div className="mt-1.5 flex items-center justify-between text-[9px] text-slate-300">
          <span className="truncate pr-2">
            {isFull ? "No seats left" : `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left`}
          </span>
          <span className="inline-flex items-center gap-1 text-emerald-300/95 opacity-90">
            Enter <span className="translate-y-[0.5px]">↗</span>
          </span>
        </div>

        <div className="mt-1.5 relative h-[5px] rounded-full bg-slate-800 overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300"
            style={{ width: `${fill}%` }}
          />
        </div>
      </div>

      {/* =========================
          DESKTOP / TABLET (>=sm)
          Keep your original sizing/structure
         ========================= */}
      <div className="hidden sm:block">
        {/* top row: avatar + host + status/platform */}
        <div className="flex items-center gap-2 sm:gap-2.5">
          <img
            src={avatarUrl}
            alt={hostName}
            className="h-6 w-6 sm:h-7 sm:w-7 rounded-full border border-white/40 object-cover"
          />

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1 sm:gap-1.5 min-w-0">
              <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.16em] text-slate-400">
                Host
              </span>
              <span className="text-[9px] sm:text-[10px] font-medium truncate">
                {hostName}
              </span>
            </div>
          </div>

          {/* status + public/private + platform cluster */}
          <div className="flex flex-col items-end gap-0.5 ml-1">
            <span
              className={`
                inline-flex items-center gap-0.5 sm:gap-1 rounded-full px-1.5 sm:px-2 py-0.5 text-[7px] sm:text-[8px] uppercase tracking-[0.18em]
                ${
                  isLive
                    ? "bg-red-500 text-white"
                    : isFull
                    ? "bg-slate-600 text-slate-50"
                    : "bg-emerald-400 text-slate-950"
                }
              `}
            >
              <span className="h-1 w-1 sm:h-1.5 sm:w-1.5 rounded-full bg-white" />
              {isLive ? "Live" : isFull ? "Full" : "Idle"}
            </span>

            <span className={`rounded-full px-1.5 sm:px-2 py-0.5 text-[7px] sm:text-[8px] uppercase tracking-[0.16em] ${
              isPublic
                ? "bg-slate-950/80 border border-emerald-400/40 text-emerald-200"
                : "bg-slate-950/80 border border-purple-400/40 text-purple-200"
            }`}>
              {isPublic ? "Public" : "Private"}
            </span>

            <span
              className={`rounded-full px-1.5 sm:px-2 py-0.5 text-[7px] sm:text-[8px] uppercase tracking-[0.16em] ${platformMeta.cls}`}
            >
              {platformMeta.label}
            </span>
          </div>
        </div>

        {/* middle: anime title + capacity */}
        <div className="mt-2 sm:mt-2.5">
          <p className="text-[10px] sm:text-[11px] font-semibold text-slate-50 truncate mb-0.5">
            {animeTitle}
          </p>

          <div className="flex items-center justify-between text-[8px] sm:text-[9px] text-slate-300 mb-1">
            <span>
              {isFull
                ? "No seats left"
                : `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left`}
            </span>
            <span className="text-slate-400">
              {used}/{total} joined
            </span>
          </div>

          <div className="relative h-1 sm:h-[5px] rounded-full bg-slate-800 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 group-hover:from-emerald-300 group-hover:to-emerald-200 transition-[width]"
              style={{ width: `${fill}%` }}
            />
          </div>
        </div>

        {/* bottom: micro CTA / vibe line */}
        <div className="mt-1.5 sm:mt-2 flex items-center justify-between text-[8px] sm:text-[9px]">
          <span className="text-slate-400 truncate hidden sm:inline">
            Matchmaking • chill chat • open mic
          </span>
          <span className="text-slate-400 truncate sm:hidden">Chill chat</span>
          <span className="inline-flex items-center gap-1 text-emerald-300/95 opacity-0 group-hover:opacity-100 transition-opacity text-[7px] sm:text-[8px]">
            Enter party
            <span className="translate-y-[0.5px]">↗</span>
          </span>
        </div>
      </div>
    </button>
  );
}

export default WatchPartyCard;
